/* storybook-check-ignore */
import Head from 'next/head';
import { Modal, ModalProps } from '@opendoor/bricks/complex';
import { ExclusiveTourRequestTimeSlot, ExclusiveTourRequest, arrayIncludeSlot } from './util';
import { Dispatch, SetStateAction, useState, useMemo } from 'react';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import ArrowLeft from '@opendoor/bricks/core/Icon/SpritesheetIcons/ArrowLeft';
import ArrowRight from '@opendoor/bricks/core/Icon/SpritesheetIcons/ArrowRight';
import ChevronRight from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronRight';
import { Flex, Button, Icon, Text } from '@opendoor/bricks/core';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { exclusives } from '@opendoor/bricks/theme';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import ReserveATourSelectSlots from './ReserveATourSelectSlots';
import AccountConnector, { AccountProps } from 'components/shared/AccountConnector';
import ReserveATourAuthentication from './ReserveATourAuthentication';
import ReserveATourConfirmation from './ReserveATourConfirmation';
import omit from 'lodash/omit';
import ReserveATourSelectSingleSpot from './ReserveATourSelectSingleSlot';
import { mediaBreakpoints } from 'helpers/exclusives/pdpMediaBreakpoints';
import { RequestATourError, useRequestATour } from './useRequestATour';

const MotionFlex = motion(Flex);
const modalCssPadding = `
  .ReactModal__Content {
    max-height: calc(var(--doc-height) - 48px) !important;
  }
  .ReactModal__Content  > div:nth-of-type(2) {
    padding: var(--space-7) var(--space-7);
    padding-top: 0;
  }
  @media (min-width: ${mediaBreakpoints.MD}) {
    .ReactModal__Content  > div:nth-of-type(2) {
      padding: var(--space-8) var(--space-11);
      padding-top: var(--space-2);
    }
  }
`;

type InheritedProps = Required<Pick<ModalProps, 'isOpen' | 'onRequestClose'>> &
  Pick<
    ReturnType<typeof useRequestATour>,
    | 'isInstantSlot'
    | 'createTourRequest'
    | 'closestSlots'
    | 'setSelectedSlots'
    | 'instantSlots'
    | 'slots'
    | 'canCreateTourRequest'
    | 'reportEvent'
    | 'listing'
    | 'tourableListings'
    | 'hasTourRequest'
  >;
interface Props extends InheritedProps {
  selectedSlots: ExclusiveTourRequestTimeSlot[];
  initialSelectingSpotState: ReserveATourState.SelectSingleSpot | ReserveATourState.SelectingSlots;
  canCreateTourRequest: boolean;
  isTourRequestLoading: boolean;
  isTourRequestError: boolean;
  error?: RequestATourError;
  tourRequestData: ExclusiveTourRequest | null | undefined;
}

export enum ReserveATourState {
  SelectSingleSpot = 'SelectSingleSpot',
  SelectingSlots = 'SelectingSlots',
  Authentication = 'Authentication',
  Confirmation = 'Confirmation',
}

const TITLE_BY_STATE: Record<ReserveATourState, string> = {
  [ReserveATourState.SelectSingleSpot]: '',
  [ReserveATourState.SelectingSlots]: 'Self tour this home',
  [ReserveATourState.Authentication]: 'Sign in to tour this home',
  [ReserveATourState.Confirmation]: 'Tour request',
};

const LEFT_COMPONENT_BY_STATE: Record<
  ReserveATourState,
  | ((
      setStepState: Dispatch<SetStateAction<ReserveATourState>>,
      initialSelectingSpotState: Props['initialSelectingSpotState'],
      closeModal?: () => void,
    ) => JSX.Element)
  | undefined
> = {
  [ReserveATourState.SelectSingleSpot]: () => (
    <Text fontSize="s2" fontWeight="bold">
      Self tour this home
    </Text>
  ),
  [ReserveATourState.SelectingSlots]: undefined,
  [ReserveATourState.Authentication]: (setStepState, initialSelectingSpotState, closeModal) => (
    <Button
      variant="icon"
      aria-label="Back to slot selection"
      analyticsName=""
      onClick={() => closeModal?.() || setStepState(initialSelectingSpotState)}
    >
      <Icon name="arrow-left" />
    </Button>
  ),
  [ReserveATourState.Confirmation]: undefined,
};

const SLIDE_IN_VARIANTS: Variants = {
  left: { x: -300, opacity: 0 },
  right: { x: 300, opacity: 0 },
  visible: { x: 0, opacity: 1 },
};

export function ReserveATourModal(props: Props & AccountProps) {
  const isAuthenticated = !props.infoIsLoading && props.loggedIn;
  const device = useDevice();
  const shouldCloseModalOnBack = useMemo(() => {
    return props.selectedSlots.some((s) => !props.slots.includes(s));
  }, [props.selectedSlots, props.slots]);
  const [stepState, setStepState] = useState(() => {
    const selectedInstantSpot = props.selectedSlots.some((slot) =>
      arrayIncludeSlot(props.instantSlots, slot),
    );
    let initialStep: ReserveATourState;
    if (selectedInstantSpot) {
      initialStep = isAuthenticated
        ? ReserveATourState.Confirmation
        : ReserveATourState.Authentication;
    } else {
      initialStep = props.initialSelectingSpotState;
    }
    return initialStep;
  });
  const account = omit(
    props,
    'closestSlots',
    'listing',
    'selectedSlots',
    'setSelectedSlots',
    'slots',
    'isOpen',
    'onRequestClose',
    'reportEvent',
    'initialSelectingSpotState',
  );
  const onRequestTimeForTourClick = () => {
    const goTo = account.loggedIn
      ? ReserveATourState.Confirmation
      : ReserveATourState.Authentication;
    setStepState(goTo);
    props.reportEvent('select-times', {
      slots: props.selectedSlots,
    });
  };
  const onLogin = () => {
    setStepState(ReserveATourState.Confirmation);
    props.reportEvent('login', {
      slots: props.selectedSlots,
    });
  };
  const onSingleSpotSelected: Parameters<typeof ReserveATourSelectSingleSpot>['0']['onCompleted'] =
    ({ slot, contextualData }) => {
      if (!slot) {
        setStepState(ReserveATourState.SelectingSlots);
      } else {
        props.setSelectedSlots([slot]);
        setStepState(
          arrayIncludeSlot(props.instantSlots, slot)
            ? account.loggedIn
              ? ReserveATourState.Confirmation
              : ReserveATourState.Authentication
            : ReserveATourState.SelectingSlots,
        );
      }
      props.reportEvent('request-a-tour-select-single-spot', {
        slot: slot ? [slot] : [],
        ...contextualData,
      });
    };
  return (
    <>
      <Head>
        <style
          dangerouslySetInnerHTML={{
            __html: modalCssPadding,
          }}
        />
      </Head>
      <Modal
        analyticsName="cosmos-exclusives-reserve-a-tour-modal"
        headerProps={{
          title: TITLE_BY_STATE[stepState],
          leftComponent: LEFT_COMPONENT_BY_STATE[stepState]?.(
            setStepState,
            props.initialSelectingSpotState,
            shouldCloseModalOnBack ? props.onRequestClose : undefined,
          ),
        }}
        desktopAndTabletSize="lg"
        mobileSize="sheet"
        isCloseSticky
        {...props}
      >
        <SelectiveSpritesheet icons={[ArrowLeft, ArrowRight, ChevronRight]} />
        <ThemedLite theme={exclusives}>
          <AnimatePresence exitBeforeEnter presenceAffectsLayout initial={false}>
            <MotionFlex
              flexDirection="column"
              height="100%"
              position="relative"
              gridGap={
                [ReserveATourState.Confirmation, ReserveATourState.Authentication].includes(
                  stepState,
                )
                  ? '1rem'
                  : '2rem'
              }
              exit="right"
              initial="left"
              layout="position"
              animate="visible"
              variants={SLIDE_IN_VARIANTS}
              key={`ReserveATourState.${stepState}`}
            >
              {stepState === ReserveATourState.SelectSingleSpot && (
                <ReserveATourSelectSingleSpot
                  closestSlots={props.closestSlots}
                  instantSlots={props.instantSlots}
                  onCompleted={onSingleSpotSelected}
                  listing={props.listing}
                  reportEvent={props.reportEvent}
                  hasTourRequest={props.hasTourRequest}
                  requestedTourSlots={props.tourRequestData?.exclusive_tour_request_time_slots}
                />
              )}
              {stepState === ReserveATourState.SelectingSlots && (
                <ReserveATourSelectSlots
                  {...props}
                  device={device}
                  onRequestTimeForTourClick={onRequestTimeForTourClick}
                />
              )}
              {stepState === ReserveATourState.Authentication && (
                <ReserveATourAuthentication
                  onLogin={onLogin}
                  onSelectionExpired={props.onRequestClose}
                />
              )}
              {stepState === ReserveATourState.Confirmation && (
                <ReserveATourConfirmation {...props} onError={props.onRequestClose} />
              )}
            </MotionFlex>
          </AnimatePresence>
        </ThemedLite>
      </Modal>
    </>
  );
}

export default AccountConnector(ReserveATourModal);
